import React, { useEffect, useId, useState } from 'react'
import toast from 'react-hot-toast'
import { XMarkIcon, CreditCardIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import { PaymentMethod, Subscription, SubscriptionTier } from '../../types'
import { monetizationAPI } from '../../services/api'
import { clearPendingReferral, pendingReferralFor } from '../../utils/referral'
import { completeCardPayment } from '../../services/cardPayment'
import useEscapeKey from '../../hooks/useEscapeKey'
import Spinner from '../Spinner'
import PaymentMethodForm from './PaymentMethodForm'
import { SecurePaymentsLine, TestModeNotice } from './StripeTrust'

interface SubscribeModalProps {
  isOpen: boolean
  onClose: () => void
  tier: SubscriptionTier | null
  billingCycle: 'monthly' | 'yearly'
  onSubscribed?: (subscription: Subscription) => void
}

const SubscribeModal: React.FC<SubscribeModalProps> = ({
  isOpen,
  onClose,
  tier,
  billingCycle,
  onSubscribed,
}) => {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([])
  const [selectedMethodId, setSelectedMethodId] = useState<string | null>(null)
  const [isLoadingMethods, setIsLoadingMethods] = useState(false)
  const [showAddForm, setShowAddForm] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [discordUserId, setDiscordUserId] = useState('')
  const [telegramUserId, setTelegramUserId] = useState('')
  const [inviteLink, setInviteLink] = useState<string | null>(null)
  const titleId = useId()

  useEffect(() => {
    if (isOpen && tier) {
      loadPaymentMethods()
    } else {
      setSelectedMethodId(null)
      setShowAddForm(false)
      setError(null)
      setDiscordUserId('')
      setTelegramUserId('')
      setInviteLink(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, tier])

  const loadPaymentMethods = async () => {
    setIsLoadingMethods(true)
    try {
      const methods = await monetizationAPI.getPaymentMethods()
      setPaymentMethods(methods)
      const defaultMethod = methods.find((m) => m.is_default) || methods[0]
      setSelectedMethodId(defaultMethod ? defaultMethod.id : null)
      setShowAddForm(methods.length === 0)
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load payment methods')
    } finally {
      setIsLoadingMethods(false)
    }
  }

  const handleAddPaymentMethod = async (method: any) => {
    try {
      const added = await monetizationAPI.addPaymentMethod(method)
      toast.success('Payment method added')
      await loadPaymentMethods()
      setSelectedMethodId(added.id)
      setShowAddForm(false)
    } catch (err: any) {
      throw new Error(err.response?.data?.detail || 'Failed to add payment method')
    }
  }

  const handleClose = () => {
    if (isSubmitting) return
    onClose()
  }

  const handleSubscribe = async () => {
    if (!tier || !selectedMethodId) return

    setError(null)
    setIsSubmitting(true)
    try {
      const subscription = await monetizationAPI.subscribe(
        tier.id,
        selectedMethodId,
        billingCycle === 'yearly',
        discordUserId.trim() || undefined,
        telegramUserId.trim() || undefined,
        pendingReferralFor(tier.creator_id)
      )
      // 3-D Secure: the bank's approval step, when the card asks for one.
      const outcome = await completeCardPayment(subscription)
      if (outcome.status === 'failed') {
        setError(outcome.message)
        return
      }
      toast.success(
        subscription.payment_status === 'requires_action'
          ? `Approved. Your ${tier.name} subscription starts in a moment.`
          : `Subscribed to ${tier.name}!`
      )
      // The link that brought them here has done its job.
      if (pendingReferralFor(tier.creator_id)) clearPendingReferral()
      onSubscribed?.(subscription)
      if (subscription.telegram_invite_link) {
        setInviteLink(subscription.telegram_invite_link)
      } else {
        onClose()
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to subscribe')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCopyInviteLink = async () => {
    if (!inviteLink) return
    try {
      await navigator.clipboard.writeText(inviteLink)
      toast.success('Invite link copied')
    } catch {
      toast.error('Could not copy link')
    }
  }

  useEscapeKey(handleClose, isOpen)

  if (!isOpen || !tier) return null

  const priceCents =
    billingCycle === 'yearly' && tier.price_yearly_cents != null
      ? tier.price_yearly_cents
      : tier.price_monthly_cents

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 id={titleId} className="text-xl font-bold text-gray-900">
            {inviteLink ? "You're subscribed!" : 'Confirm Subscription'}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-600 hover:text-gray-800 transition-colors"
            aria-label="Close modal"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {inviteLink ? (
          <div className="p-6 space-y-6">
            <p className="text-sm text-gray-600">
              Join {tier.name}'s Telegram group using your invite link below:
            </p>
            <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-md p-3">
              <input
                type="text"
                readOnly
                value={inviteLink}
                className="flex-1 bg-transparent text-sm text-gray-900 outline-none"
              />
              <button
                type="button"
                onClick={handleCopyInviteLink}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium whitespace-nowrap"
              >
                Copy
              </button>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
            >
              Done
            </button>
          </div>
        ) : (
        <div className="p-6 space-y-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="font-semibold text-gray-900">{tier.name}</p>
            {tier.description && <p className="text-sm text-gray-600">{tier.description}</p>}
            <p className="mt-2 text-2xl font-bold text-gray-900">
              ${(priceCents / 100).toFixed(2)}
              <span className="text-sm font-normal text-gray-600 ml-1">
                /{billingCycle === 'yearly' ? 'year' : 'month'}
              </span>
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          {isLoadingMethods ? (
            <div className="flex justify-center py-6">
              <Spinner size="md" />
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Payment Method
              </label>

              {paymentMethods.length > 0 && (
                <div className="space-y-2 mb-3">
                  {paymentMethods.map((method) => (
                    <label
                      key={method.id}
                      className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedMethodId === method.id
                          ? 'border-blue-500 ring-1 ring-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="payment-method"
                        value={method.id}
                        checked={selectedMethodId === method.id}
                        onChange={() => setSelectedMethodId(method.id)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <CreditCardIcon className="h-5 w-5 text-gray-600 flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-900">
                        {method.brand || 'Card'} ****{method.last4}
                      </span>
                      {method.is_default && (
                        <span className="inline-flex items-center text-xs text-green-600 ml-auto">
                          <CheckCircleIcon className="h-3 w-3 mr-1" />
                          Default
                        </span>
                      )}
                    </label>
                  ))}
                </div>
              )}

              {!showAddForm ? (
                <button
                  type="button"
                  onClick={() => setShowAddForm(true)}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  + Add a payment method
                </button>
              ) : (
                <PaymentMethodForm onAddPaymentMethod={handleAddPaymentMethod} />
              )}
            </div>
          )}

          <div className="space-y-3">
            <div>
              <label htmlFor="discord-user-id" className="block text-sm font-medium text-gray-700">
                Discord User ID <span className="text-gray-400">(optional)</span>
              </label>
              <input
                id="discord-user-id"
                type="text"
                value={discordUserId}
                onChange={(e) => setDiscordUserId(e.target.value)}
                placeholder="e.g. 123456789012345678"
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Enable Developer Mode in Discord, then right-click your profile picture and choose
                "Copy User ID" to auto-receive this creator's subscriber role.
              </p>
            </div>
            <div>
              <label htmlFor="telegram-user-id" className="block text-sm font-medium text-gray-700">
                Telegram User ID <span className="text-gray-400">(optional)</span>
              </label>
              <input
                id="telegram-user-id"
                type="text"
                value={telegramUserId}
                onChange={(e) => setTelegramUserId(e.target.value)}
                placeholder="e.g. 123456789"
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Message @userinfobot on Telegram to find your numeric ID. We'll use it to remove
                you from the group automatically if you cancel.
              </p>
            </div>
          </div>

          {/* The add-card form carries these itself. */}
          {!showAddForm && (
            <div className="space-y-2">
              <TestModeNotice />
              <SecurePaymentsLine />
            </div>
          )}

          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubscribe}
              disabled={isSubmitting || !selectedMethodId}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting && <Spinner size="sm" className="border-white border-t-transparent" />}
              {isSubmitting ? 'Subscribing' : 'Subscribe'}
            </button>
          </div>
        </div>
        )}
      </div>
    </div>
  )
}

export default SubscribeModal
